/**
 * Real API service for visitor pre-registration endpoints
 */

import api from './api';

export interface Block {
  id: string;
  name: string;
  units: Unit[];
}

export interface Unit {
  id: string;
  number: string;
}

export interface Department {
  id: string;
  name: string;
}

export interface ValidateQRResponse {
  valid: boolean;
  customer_id?: string;
  customer_name?: string;
  customer_type?: string;
  blocks?: Block[];
  departments?: Department[];
  error?: string;
}

export interface PreRegistrationSubmitRequest {
  token: string;
  first_name: string;
  last_name: string;
  cpf?: string;
  phone?: string;
  visiting_unit_id?: string;
  visiting_department_id?: string;
  comment?: string;
  photo_base64?: string;
  /**
   * The uncropped photo behind photo_base64. Optional: the backend stores it best-effort and
   * ignores its absence, so older clients that never send it keep working unchanged.
   */
  photo_original_base64?: string;
  captcha_token: string;
}

export interface PreRegistrationSubmitResponse {
  success: boolean;
  id?: string;
  message?: string;
  error?: string;
}

export interface PreRegistration {
  id: string;
  customer_id: string;
  first_name: string;
  last_name: string;
  cpf?: string;
  phone?: string;
  profile_picture_url?: string;
  visiting_unit_id?: string;
  visiting_block_id?: string;
  visiting_unit_number?: string;
  visiting_block_name?: string;
  visiting_department_id?: string;
  visiting_department_name?: string;
  comment?: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  qr_token_id: string;
  converted_to_person_id?: string;
  converted_to_role_id?: string;
  converted_at?: string;
  converted_by?: string;
  rejected_at?: string;
  rejected_by?: string;
  rejection_reason?: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
  updated_at: string;
  expires_at: string;
}

export interface ApprovalData {
  authorized_by_resident_id?: string;
  authorized_by_employee_id?: string;
  authorized_by_name?: string;
  vehicle_plate?: string;
  access_type: 'single_visit' | 'multiple_visits';
  additional_notes?: string;
}

export interface QRCode {
  id: string;
  customer_id?: string;
  customer_name?: string;
  token: string;
  token_type?: 'visitor_pre_registration';
  is_active?: boolean;
  max_uses_per_day?: number;
  uses_today: number;
  total_uses?: number;
  last_used_at?: string;
  expires_at?: string;
  created_at: string;
  created_by?: string;
  deactivated_at?: string;
  deactivated_by?: string;
  qr_code_url?: string;
}

/**
 * Validate QR token and return customer context
 */
export const validateQRToken = async (token: string): Promise<ValidateQRResponse> => {
  try {
    const response = await api.get(`/public/pre-registration/validate/${token}`);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return {
        valid: false,
        error: 'Token inválido ou expirado',
      };
    }
    throw error;
  }
};

/**
 * Submit visitor pre-registration
 */
export const submitPreRegistration = async (
  request: PreRegistrationSubmitRequest
): Promise<PreRegistrationSubmitResponse> => {
  try {
    const response = await api.post('/public/pre-registration', request);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erro ao enviar pré-registro',
    };
  }
};

/**
 * Get all pending pre-registrations for a customer
 */
export const getPendingPreRegistrations = async (customerId: string): Promise<PreRegistration[]> => {
  try {
    const response = await api.get(`/customers/${customerId}/pre-registrations`, {
      params: { status: 'pending' },
      // Suppress toast for permission failures (e.g. síndico role lacks access)
      _silentOnError: true,
    } as any);
    return response.data.items || [];
  } catch (error) {
    return [];
  }
};

/**
 * Approve a pre-registration
 */
export const approvePreRegistration = async (
  customerId: string,
  preRegId: string,
  approvalData: ApprovalData
): Promise<{ success: boolean; person_id?: string; role_id?: string; error?: string }> => {
  try {
    const response = await api.post(
      `/customers/${customerId}/pre-registrations/${preRegId}/approve`,
      approvalData
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erro ao aprovar pré-registro',
    };
  }
};

/**
 * Reject a pre-registration
 */
export const rejectPreRegistration = async (
  customerId: string,
  preRegId: string,
  reason: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await api.post(
      `/customers/${customerId}/pre-registrations/${preRegId}/reject`,
      { reason }
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erro ao rejeitar pré-registro',
    };
  }
};

// ===== QR CODE MANAGEMENT =====

/**
 * Get all QR codes for a customer (active + historical)
 */
export const getAllQRCodes = async (customerId: string): Promise<QRCode[]> => {
  try {
    const response = await api.get(`/customers/${customerId}/qr-codes`);
    return response.data || [];
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 404 || status === 403 || status === 401) {
      return [];
    }
    throw error;
  }
};

/**
 * Get active QR code for a customer
 */
export const getActiveQRCode = async (customerId: string): Promise<QRCode | null> => {
  try {
    const response = await api.get(`/customers/${customerId}/qr-codes/active`);
    return response.data;
  } catch (error: any) {
    const status = error.response?.status;
    if (status === 404 || status === 403 || status === 401) {
      return null;
    }
    throw error;
  }
};

/**
 * Generate a new QR code for a customer
 */
export const generateQRCode = async (
  customerId: string,
  options?: {
    max_uses_per_day?: number;
    expires_at?: string;
  }
): Promise<QRCode> => {
  const response = await api.post(`/customers/${customerId}/qr-codes`, options || {});
  return response.data;
};

/**
 * Deactivate a QR code
 */
export const deactivateQRCode = async (
  customerId: string,
  qrCodeId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await api.delete(`/customers/${customerId}/qr-codes/${qrCodeId}`);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erro ao desativar QR code',
    };
  }
};

/**
 * Update QR code max uses per day limit
 */
export const updateQRCodeLimit = async (
  customerId: string,
  qrCodeId: string,
  maxUsesPerDay?: number
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await api.patch(`/customers/${customerId}/qr-codes/${qrCodeId}/limit`, {
      max_uses_per_day: maxUsesPerDay,
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || 'Erro ao atualizar limite',
    };
  }
};
